import { io, Socket } from 'socket.io-client';
import { supabase } from './supabase';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from '../../../shared/types';

export type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
/** URL del servidor de juego (también para su API HTTP, p. ej. el borrado de cuenta). */
export const API_URL = apiUrl.replace(/\/+$/, '');

/**
 * Singleton del cliente Socket.io.
 *
 * Toda la pestaña comparte UNA conexión: el lobby, el Hub y el partido usan
 * el mismo socket, así la pertenencia a la sala sobrevive a la navegación
 * entre páginas.
 *
 *  - Se guarda en `globalThis`: cuando Vite recarga en caliente este módulo
 *    (o uno que lo importa) se reutiliza la conexión existente. Antes cada
 *    HMR abría un socket nuevo y dejaba el viejo vivo y huérfano.
 *  - El token se pide en cada (re)conexión (callback `auth`), no una sola
 *    vez: una sesión renovada no deja al socket con un token caducado.
 *  - Socket.io reconecta solo salvo cuando el servidor corta la conexión
 *    a propósito (`io server disconnect`); en ese caso reconectamos aquí.
 */
type SocketGlobal = typeof globalThis & { __sportsarenaSocket?: ClientSocket };
const globalRef = globalThis as SocketGlobal;

const resolveToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? 'dev-anon-token';
  } catch {
    return 'dev-anon-token';
  }
};

const createSocket = (): ClientSocket => {
  const socket: ClientSocket = io(apiUrl, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    auth: (cb) => {
      void resolveToken().then((token) => cb({ token }));
    },
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 3_000,
    timeout: 8_000,
  });

  socket.on('connect', () => {
    console.info(`[socket] Conectado (${socket.id})`);
  });
  socket.on('disconnect', (reason) => {
    console.warn(`[socket] Desconectado: ${reason}`);
    if (reason === 'io server disconnect') socket.connect();
  });
  socket.on('connect_error', (err) => {
    console.warn(`[socket] Error de conexión: ${err.message}`);
  });
  return socket;
};

export const getSocket = (): Promise<ClientSocket> => {
  globalRef.__sportsarenaSocket ??= createSocket();
  return Promise.resolve(globalRef.__sportsarenaSocket);
};

/** Desconecta y limpia el socket (logout). */
export const disconnectSocket = (): void => {
  globalRef.__sportsarenaSocket?.disconnect();
  globalRef.__sportsarenaSocket = undefined;
};
